import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { buyAirtime } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";

const NETWORKS = [
  { id: "MTN", label: "MTN", logo: "🟡" },
  { id: "AIRTEL", label: "Airtel", logo: "🔴" },
  { id: "GLO", label: "Glo", logo: "🟢" },
  { id: "9MOBILE", label: "9mobile", logo: "🟩" },
];

const QUICK_AMOUNTS = [100, 200, 500, 1000, 2000, 5000];

export default function AirtimePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const num = "07014970207"

  const [network, setNetwork] = useState(searchParams.get("network") || "MTN");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [isSelf, setIsSelf] = useState(true);
  const [loading, setLoading] = useState(false);

  const isValid = phone.length === 11 && Number(amount) >= 50;

  const handlePay = async () => {
    if (!isValid || loading) return;
    setLoading(true);
    try {
      const res = await buyAirtime(network, phone, Number(amount), num);

      if (res.status) {
        navigate("/bills/success", {
          state: {
            type: "airtime",
            title: `${network} Airtime`,
            detail: phone,
            amount: Number(amount),
            requestId: res.requestId,
            transactionId: res.transactionId,
          },
        });
      } else {
        toast.error(res.message || "Airtime purchase failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Airtime purchase failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-10">
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/bills")} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Buy Airtime</h1>
            <p className="text-slate-400 text-xs">Instant top-up, all networks</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-5">
        {/* Network */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Select Network</p>
          <div className="grid grid-cols-4 gap-2">
            {NETWORKS.map((net) => (
              <button
                key={net.id}
                onClick={() => setNetwork(net.id)}
                className={`flex flex-col items-center gap-2 py-3 rounded-2xl border-2 transition-all ${
                  network === net.id ? "border-emerald-500 bg-emerald-50" : "border-slate-100 bg-white"
                }`}
              >
                <span className="text-2xl">{net.logo}</span>
                <span className="text-xs font-semibold text-slate-700">{net.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Phone */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-slate-600 text-sm font-semibold">Phone Number</p>
            <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
              {["Myself", "Others"].map((label, i) => (
                <button
                  key={label}
                  onClick={() => { setIsSelf(i === 0); setPhone(""); }}
                  className={`text-xs px-3 py-1 rounded-lg font-medium transition-all ${
                    (i === 0) === isSelf ? "bg-white text-emerald-600 shadow-sm" : "text-slate-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white border-2 border-slate-200 rounded-2xl flex items-center px-4 focus-within:border-emerald-400 transition-colors">
            <span className="text-slate-400 text-sm mr-2">🇳🇬 +234</span>
            <div className="w-px h-5 bg-slate-200 mr-3" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 11))}
              placeholder="0801 234 5678"
              className="flex-1 py-4 outline-none text-slate-800 text-sm bg-transparent placeholder-slate-300"
            />
            {phone.length === 11 && <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />}
          </div>
        </div>

        {/* Amount */}
        <div>
          <p className="text-slate-600 text-sm font-semibold mb-3">Amount</p>
          <div className="bg-white border-2 border-slate-200 rounded-2xl flex items-center px-4 focus-within:border-emerald-400 transition-colors mb-3">
            <span className="text-slate-400 font-bold mr-2">₦</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount (min ₦50)"
              className="flex-1 py-4 outline-none text-slate-800 text-sm bg-transparent placeholder-slate-300"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_AMOUNTS.map((amt) => (
              <button
                key={amt}
                onClick={() => setAmount(String(amt))}
                className={`py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                  amount === String(amt)
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
                }`}
              >
                ₦{amt.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        {isValid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 space-y-2">
            {[
              { label: "Network", value: network },
              { label: "Number", value: phone },
              { label: "Amount", value: `₦${Number(amount).toLocaleString()}`, bold: true },
            ].map((row) => (
              <div key={row.label} className="flex justify-between text-sm">
                <span className="text-slate-500">{row.label}</span>
                <span className={row.bold ? "font-bold text-emerald-600" : "font-semibold text-slate-800"}>{row.value}</span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={handlePay}
          disabled={!isValid || loading}
          className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing…</>
          ) : isValid ? `Pay ₦${Number(amount).toLocaleString()}` : "Enter details to continue"}
        </button>
      </div>
    </div>
  );
}
